import { Injectable } from '@angular/core';
import { ApiMode } from '../../../environments/environment.model';
import { environment } from '../../../environments/environment';

/**
 * Which API servers this build talks to. Overridable in TestBed — Angular Vitest
 * does not support `vi.mock` of env modules.
 *
 * The three modes are exclusive, with no per-route hybrid:
 * - `mock`: every `/api/*` route, Dealer and Resolve included, is served by EBM.
 * - `dev` / `prod`: every route goes to that gateway. Nothing is mocked, and the
 *   EBM-only families (`/api/agent`, `/api/pages`, `/api/ops`, `/api/sync`,
 *   `/api/auth`) are not called at all — see {@link mockApiAvailable}.
 */
@Injectable({ providedIn: 'root' })
export class ApiRuntimeConfig {
  apiMode(): ApiMode {
    return environment.apiMode;
  }

  useMocks(): boolean {
    return this.apiMode() === 'mock';
  }

  /**
   * Whether the EBM-only endpoint families are reachable. They have no equivalent
   * on the real gateways, so callers must degrade rather than request them in
   * `dev`/`prod` — otherwise every one 404s and the UI reads it as a failure.
   */
  mockApiAvailable(): boolean {
    return this.apiMode() === 'mock';
  }

  dealerApiBaseUrl(): string {
    return environment.dealerApiBaseUrl;
  }

  dealerEventsTop(): number {
    return environment.dealerEventsTop ?? 200;
  }

  /** TEMP dev override — see `environment.dealerBypassAllowedSitesFilter`. */
  dealerBypassAllowedSitesFilter(): boolean {
    return environment.dealerBypassAllowedSitesFilter === true;
  }

  resolveApiBaseUrl(): string {
    return environment.resolveApiBaseUrl ?? '/api/resolve';
  }

  doriApiBaseUrl(): string {
    return environment.doriApiBaseUrl ?? '/api/dori';
  }

  /**
   * Shared Dori APP_API_TOKEN (Bearer) for AI feedback.
   * Do NOT commit real tokens — see environment.doriApiToken comment.
   */
  doriApiToken(): string {
    return environment.doriApiToken ?? '';
  }

  streamingInitializeBaseUrl(): string {
    return environment.streaming.initializeBaseUrl;
  }

  isProduction(): boolean {
    return environment.production;
  }

  /**
   * Whether a URL should be served from mock fixtures / in-process mocks.
   * Only `mock` mode mocks anything: `dev` and `prod` always hit their gateway,
   * so a broken dev route surfaces as a real failure instead of silent mock data.
   */
  shouldMockHttp(url: string): boolean {
    if (this.isLiveGatewayUrl(url)) {
      return false;
    }
    return this.useMocks();
  }

  /** True when the request targets Dealer, Resolve, Dori, or STS Auth. */
  isLiveGatewayUrl(url: string): boolean {
    return (
      url.includes('/api/dealer') ||
      url.includes('/api/resolve') ||
      url.includes('/dealer') ||
      url.includes('/resolve') ||
      url.includes('/dori') ||
      url.includes('/Auth/') ||
      url.startsWith(this.streamingInitializeBaseUrl())
    );
  }
}
